import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import WheelChart from '../components/WheelChart';
import { defaultDomains, reflectionQuestions } from '../mock';
import { ArrowRight, Download, Save } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { generateAssessmentPDF } from '../utils/pdfGenerator';
import { assessmentAPI } from '../services/api';

const AssessmentPage = () => {
  const [domains, setDomains] = useState(() => {
    const saved = localStorage.getItem('wheelAssessment');
    if (saved) {
      const savedDomains = JSON.parse(saved);
      // Merge saved data with questions from baseDomain
      return savedDomains.map((savedDomain) => {
        // Find the base domain to get questions
        const baseDomainData = defaultDomains.find(d => d.name === savedDomain.baseDomain);
        return {
          ...savedDomain,
          baseDomain: savedDomain.baseDomain || savedDomain.name,
          customName: savedDomain.customName || savedDomain.name,
          questions: baseDomainData?.questions || savedDomain.questions || []
        };
      });
    }
    // Initialize with default domains
    return defaultDomains.map(domain => ({
      ...domain,
      baseDomain: domain.name,
      customName: domain.name
    }));
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    localStorage.setItem('wheelAssessment', JSON.stringify(domains));
  }, [domains]);

  const handleScoreChange = (index, newScore) => {
    const updated = [...domains];
    updated[index].score = newScore[0];
    setDomains(updated);
  };

  const handleNext = () => {
    if (currentStep < domains.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowResults(true);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleDomainClick = (index) => {
    setCurrentStep(index);
    setShowResults(false);
  };

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      toast({
        title: 'Login Required',
        description: 'Please login to save your assessment.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await assessmentAPI.create({ domains });
      toast({
        title: 'Assessment Saved!',
        description: 'Your wheel assessment has been saved successfully.',
      });
    } catch (error) {
      toast({
        title: 'Save Failed',
        description: error.response?.data?.detail || 'Could not save assessment',
        variant: 'destructive',
      });
    }
  };

  const handleDownload = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userName = user.name || 'User';
      const fileName = generateAssessmentPDF(domains, userName);
      
      toast({
        title: 'Download Complete!',
        description: `Your assessment has been saved as ${fileName}`,
      });
    } catch (error) {
      toast({
        title: 'Download Failed',
        description: 'There was an error generating your PDF. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const calculateAverage = () => {
    const sum = domains.reduce((acc, d) => acc + d.score, 0);
    return (sum / domains.length).toFixed(1);
  };

  const getLowestDomains = () => {
    return [...domains]
      .sort((a, b) => a.score - b.score)
      .slice(0, 3);
  };

  if (showResults) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Your Wheel of Life Results
            </h1>
            <p className="text-lg text-gray-600">
              Average Score: <span className="font-bold text-green-600">{calculateAverage()}/10</span>
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            <Card className="p-8 bg-white shadow-xl">
              <WheelChart domains={domains} onDomainClick={handleDomainClick} editable={true} />
            </Card>

            <div className="space-y-6">
              <Card className="p-6 bg-white shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Your Scores</h3>
                <div className="space-y-3">
                  {domains.map((domain) => (
                    <div key={domain.id} className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: domain.color }}
                        />
                        <span className="text-sm font-medium text-gray-700">{domain.customName || domain.name}</span>
                      </div>
                      <span className="font-bold text-gray-900">{domain.score}/10</span>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Areas for Growth</h3>
                <p className="text-sm text-gray-600 mb-4">
                  These areas could benefit from more attention:
                </p>
                <div className="space-y-2">
                  {getLowestDomains().map((domain) => (
                    <div key={domain.id} className="flex items-center space-x-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: domain.color }}
                      />
                      <span className="text-sm font-medium text-gray-800">
                        {domain.customName || domain.name} ({domain.score}/10)
                      </span>
                    </div>
                  ))}
                </div>
              </Card>

              <div className="flex gap-3">
                <Button
                  onClick={handleSave}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white shadow-lg"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Assessment
                </Button>
                <Button
                  onClick={handleDownload}
                  variant="outline"
                  className="flex-1 border-2 border-green-600 text-green-600 hover:bg-green-50"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>
          </div>

          <Card className="p-6 bg-gradient-to-r from-green-600 to-green-700 text-white shadow-xl">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold mb-2">Want deeper insights?</h3>
                <p className="text-green-100">
                  Unlock self-coaching tools and AI-powered guidance with Premium
                </p>
              </div>
              <Button
                onClick={() => window.location.href = '/premium'}
                className="bg-white text-green-600 hover:bg-gray-100 shadow-lg"
              >
                Go Premium
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>

          {/* Reflection Section */}
          <Card className="p-8 bg-white shadow-xl mt-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Analysis & Reflection</h3>
            <p className="text-gray-600 mb-6">
              Take a moment to reflect on your results with these questions:
            </p>
            <div className="space-y-4">
              {reflectionQuestions.map((question, index) => (
                <div key={index} className="border-l-4 border-green-500 pl-4 py-2">
                  <p className="text-gray-800 font-medium">{index + 1}. {question}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-700">
                💡 <strong>Tip:</strong> Consider journaling your responses or discussing them with a trusted friend, coach, or therapist.
              </p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const currentDomain = domains[currentStep];
  const progress = ((currentStep + 1) / domains.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <h1 className="text-3xl font-bold text-gray-900">Life Assessment</h1>
            <span className="text-sm font-medium text-gray-600">
              {currentStep + 1} of {domains.length}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <Card className="p-8 bg-white shadow-xl mb-6">
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: currentDomain.color }}
              />
              <h2 className="text-2xl font-bold text-gray-900">{currentDomain.customName || currentDomain.name}</h2>
            </div>
            <p className="text-gray-600 mb-4">
              On a scale of 0-10, how satisfied are you with this area of your life?
            </p>
            
            {/* Self-Reflection Questions */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm font-semibold text-gray-700 mb-3">Consider these questions:</p>
              <div className="space-y-2">
                {currentDomain.questions && currentDomain.questions.map((question, idx) => (
                  <div key={idx} className="flex items-start space-x-2">
                    <span className="text-xs font-bold text-gray-500 mt-0.5">{idx + 1}.</span>
                    <p className="text-sm text-gray-600 italic">{question}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm text-gray-600">Not Satisfied</span>
              <span className="text-3xl font-bold" style={{ color: currentDomain.color }}>
                {currentDomain.score}
              </span>
              <span className="text-sm text-gray-600">Very Satisfied</span>
            </div>
            <Slider
              value={[currentDomain.score]}
              onValueChange={(value) => handleScoreChange(currentStep, value)}
              max={10}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <span key={num}>{num}</span>
              ))}
            </div>
          </div>

          <div className="flex justify-between">
            <Button
              onClick={handlePrevious}
              disabled={currentStep === 0}
              variant="outline"
              className="border-2 border-gray-300"
            >
              Previous
            </Button>
            <Button
              onClick={handleNext}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {currentStep === domains.length - 1 ? 'View Results' : 'Next'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </Card>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Preview</h3>
          <div className="max-w-sm mx-auto">
            <WheelChart domains={domains} onDomainClick={handleDomainClick} editable={false} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentPage;
